import React, { useCallback, useEffect } from 'react';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router';
import { TABLE_COLUMNS } from './constants';
import { useInjectSaga } from '../../utils/injectSaga';
import saga from './saga';
import { useInjectReducer } from '../../utils/injectReducer';
import reducer from './reducer';
import { getTrailersListAction, getTrailerTypesAction, resetStateAction, getListTrailerFileAction } from './actions';
import {
	getIsTrailersListLoadingSelector,
	getPaginationSelector,
	getTableSearchSelector,
	getTrailersListSelector,
	getTrailerTypesSelector,
	getIsDownloadLoadingSelector,
} from './selectors';
import { getIsAvailableTrailersButtonsSelector } from '../profile/selectors';
import { removeModalQuery } from '../../utils/removeModalQuery';
import { DataGridComponent } from '../../components/data-grid';
import { normalizeDataGridModel } from '../../utils/normalizeDataGridModel';

const TrailersContainerWrapper = ({
	onGetTrailersList,
	trailersList,
	isTrailersListLoading,
	onGetTrailerTypesList,
	trailerTypes,
	pagination,
	onResetState,
	isAvailable,
	onGetTrailerFile,
	isDownloadLoading,
}) => {
	useInjectSaga({ key: 'trailersContainerSaga', saga });
	useInjectReducer({ key: 'trailersContainerReducer', reducer });

	const { search, pathname } = useLocation();

	const query = new URLSearchParams(search);

	const history = useHistory();

	useEffect(() => {
		onGetTrailersList({ searchQuery: removeModalQuery(query.toString()) });
		onGetTrailerTypesList();

		return onResetState();
	}, []);

	const handleAddButtonClick = () => {
		query.set('modal', 'trailer');
		query.set('mode', 'add');

		history.push(`${pathname}?${query}`);
	};

	const handlePageChange = (_, { activePage }) => {
		query.set('page', activePage);
		history.push(`${pathname}?${query}`);
		onGetTrailersList({ searchQuery: removeModalQuery(query.toString()) });
	};

	const handleSelectChange = (_, { value }) => {
		query.set('page', 1);
		if (value === 'all') {
			query.delete('per_page');
			history.push(`${pathname}?${query}`);
		} else {
			query.set('per_page', value);
			history.push(`${pathname}?${query}`);
		}
		onGetTrailersList({ searchQuery: removeModalQuery(query.toString()) });
	};

	const handleDownloadButtonClick = () => {
		onGetTrailerFile({ exportUrl: '/api/trailers/export' });
	};

	const handleSortChange = useCallback(sortModel => {
		const jsonSorts = JSON.stringify(sortModel);

		query.set('sorts', jsonSorts);

		history.push(`${pathname}?${query}`);
		onGetTrailersList({ searchQuery: removeModalQuery(query.toString()) });
	});

	const handleFilterChange = useCallback((filterModel, action) => {
		if (action.reason) {
			const jsonFilters = JSON.stringify(normalizeDataGridModel(filterModel, TABLE_COLUMNS(trailerTypes)));

			query.delete('page');
			query.set('filters', jsonFilters);

			history.push(`${pathname}?${query}`);
			onGetTrailersList({ searchQuery: removeModalQuery(query.toString()) });
		}
	});

	return (
		<>
			<DataGridComponent
				headerText="Прицепы"
				onAddButtonClick={isAvailable.create ? handleAddButtonClick : null}
				onDownloadButtonClick={isAvailable.export ? handleDownloadButtonClick : null}
				columns={TABLE_COLUMNS(trailerTypes)}
				isDownloadButtonLoading={isDownloadLoading}
				rows={trailersList}
				pagination={
					!pagination
						? null
						: { ...pagination, onPageChange: handlePageChange, onSelectChange: handleSelectChange }
				}
				isTableDataLoading={isTrailersListLoading}
				handleSortChange={handleSortChange}
				handleFilterChange={handleFilterChange}
			/>
		</>
	);
};

const mapStateToProps = createStructuredSelector({
	trailersList: getTrailersListSelector(),
	isTrailersListLoading: getIsTrailersListLoadingSelector(),
	trailerTypes: getTrailerTypesSelector(),
	pagination: getPaginationSelector(),
	tableSearch: getTableSearchSelector(),
	isAvailable: getIsAvailableTrailersButtonsSelector(),
	isDownloadLoading: getIsDownloadLoadingSelector(),
});

const mapDispatchToProps = {
	onGetTrailerFile: getListTrailerFileAction,
	onGetTrailersList: getTrailersListAction,
	onGetTrailerTypesList: getTrailerTypesAction,
	onResetState: resetStateAction,
};

const TrailersContainer = connect(mapStateToProps, mapDispatchToProps)(TrailersContainerWrapper);

TrailersContainerWrapper.propTypes = {
	onGetTrailersList: PropTypes.func.isRequired,
	onGetTrailerFile: PropTypes.func.isRequired,
	trailersList: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
			name: PropTypes.string.isRequired,
			number: PropTypes.string.isRequired,
			vin: PropTypes.string.isRequired,
			link: PropTypes.string.isRequired,
		}),
	),
	isTrailersListLoading: PropTypes.bool.isRequired,
	isDownloadLoading: PropTypes.bool.isRequired,
	onGetTrailerTypesList: PropTypes.func.isRequired,
	trailerTypes: PropTypes.array,
	pagination: PropTypes.shape({
		currentPage: PropTypes.number,
		totalPages: PropTypes.number.isRequired,
		total: PropTypes.number,
		from: PropTypes.number,
		to: PropTypes.number,
		isNextItem: PropTypes.bool,
		isPrevItem: PropTypes.bool,
		isFirstItem: PropTypes.bool,
		isLastItem: PropTypes.bool,
	}),
	onResetState: PropTypes.func.isRequired,
	isAvailable: PropTypes.objectOf(PropTypes.bool),
};

export default TrailersContainer;
